export interface NexusEvent<T = unknown> {
  id: string;
  atMs: number;
  gameId: string;
  worldId: string;
  type: string;
  source: string;
  entityId?: string;
  payload?: T;
}

export interface NexusSnapshot {
  version: 1;
  events: NexusEvent[];
}

export class LivingNexus {
  private readonly listeners = new Set<(event: NexusEvent) => void>();
  private events: NexusEvent[] = [];
  private readonly eventIds = new Set<string>();
  private serial = 0;

  constructor(private readonly maxEvents = 256) {}

  publish<T>(input: Omit<NexusEvent<T>, "id">): NexusEvent<T> {
    const event: NexusEvent<T> = {
      ...input,
      id: `${input.gameId}:${input.worldId}:${input.atMs}:${++this.serial}`,
    };
    this.ingest(event as NexusEvent);
    return event;
  }

  ingest(event: NexusEvent): boolean {
    if (!event?.id || this.eventIds.has(event.id)) return false;
    this.eventIds.add(event.id);
    this.events.push({ ...event });
    if (this.events.length > this.maxEvents) {
      const removed = this.events.splice(0, this.events.length - this.maxEvents);
      for (const item of removed) this.eventIds.delete(item.id);
    }
    for (const listener of this.listeners) listener({ ...event });
    return true;
  }

  subscribe(listener: (event: NexusEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  recent(type?: string): NexusEvent[] {
    const source = type ? this.events.filter(event => event.type === type) : this.events;
    return source.map(event => ({ ...event }));
  }

  snapshot(): NexusSnapshot {
    return { version: 1, events: this.recent() };
  }

  restore(snapshot: unknown): number {
    if (!snapshot || typeof snapshot !== "object") return 0;
    const candidate = snapshot as Partial<NexusSnapshot>;
    if (candidate.version !== 1 || !Array.isArray(candidate.events)) return 0;
    this.events = candidate.events
      .filter(event => event && typeof event.id === "string" && typeof event.type === "string")
      .slice(-this.maxEvents)
      .map(event => ({ ...event }));
    this.eventIds.clear();
    for (const event of this.events) this.eventIds.add(event.id);
    this.serial = this.events.length;
    return this.events.length;
  }

  clear(): void {
    this.events = [];
    this.eventIds.clear();
    this.serial = 0;
  }
}
